import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationPrevious, PaginationNext, PaginationEllipsis,
} from "@/components/ui/pagination";

/**
 * Page-number pagination for list pages backed by {items, total, page, limit}
 * responses. Renders nothing when everything fits on one page. Kept as a
 * single shared component so Books/Authors/Publishers/Search (and the admin
 * tables) all page the same way instead of the user being stuck on page 1
 * with no way to reach the rest of a 500+ book catalog.
 */
export function ListPagination({ page, limit, total, onPageChange, testid }) {
  const { t } = useI18n();
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;

  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onPageChange(p);
  };

  // Windowed page numbers: first, last, current +-1, with ellipses.
  const pages = new Set([1, totalPages, page, page - 1, page + 1]);
  const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);

  const items = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) items.push({ ellipsis: true, key: `e-${p}` });
    items.push({ page: p, key: `p-${p}` });
    prev = p;
  }

  return (
    <Pagination className="mt-8" data-testid={testid || "pagination"}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(e) => { e.preventDefault(); go(page - 1); }}
            aria-disabled={page === 1}
            className={page === 1 ? "pointer-events-none opacity-40" : ""}
            data-testid="pagination-prev"
            label={t("prev_page")}
          />
        </PaginationItem>
        {items.map(it => it.ellipsis ? (
          <PaginationItem key={it.key}><PaginationEllipsis /></PaginationItem>
        ) : (
          <PaginationItem key={it.key}>
            <PaginationLink
              href="#"
              isActive={it.page === page}
              onClick={(e) => { e.preventDefault(); go(it.page); }}
              data-testid={`pagination-page-${it.page}`}
            >
              {it.page}
            </PaginationLink>
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(e) => { e.preventDefault(); go(page + 1); }}
            aria-disabled={page === totalPages}
            className={page === totalPages ? "pointer-events-none opacity-40" : ""}
            data-testid="pagination-next"
            label={t("next_page")}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
